import { Component, computed, effect, inject, input, InputSignal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { EvCore } from "ev-core";
import { EvTooltipModule } from "ev-tooltip";
import { MiniInfocardImageCarouselComponent } from "src/app/ev-map/shared-components/mini-infocard-image-carousel/mini-infocard-image-carousel.component";
import { ThreeDotNavigationComponent } from "src/app/shared-components/three-dot-navigation/three-dot-navigation.component";
import { ImagePackage } from "src/models/common/image.model";
import { MiniInfocardEstateSale } from "src/models/estate/mini-infocard-estate.model";
import { EVOwnership, EVSaleCategoryType } from "src/models/units/unit-attributes.model";
import { FavoriteListDataService } from "src/services/estate/favorite-list-data.service";
import { MarketSalesDataService } from "src/services/estate/market-sales-data.service";
import { LeftSidebarService } from "src/services/left-sidebar.service";
import { MapActionsService } from "src/services/map/map-actions.service";
import { MapMode, MapStateService } from "src/services/map/map-state.service";
import { LinkHelperService, SolutionPage, UnitLinkType } from "src/services/shared/link-helper.service";
import { SelectedFavoriteListsStore } from "src/stores/selected-favorite-list.store";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { EstateSaleInfocardPropertiesComponent } from "./estate-sale-infocard-properties/estate-sale-infocard-properties.component";

@Component({
    selector: "estate-sale-infocard",
    imports: [EvCore, EvTooltipModule, MiniInfocardImageCarouselComponent, EstateSaleInfocardPropertiesComponent, ThreeDotNavigationComponent],
    templateUrl: "./estate-sale-infocard.component.html",
    styleUrl: "./estate-sale-infocard.component.scss",
})
export class EstateSaleInfocardComponent {
    selectedSale: InputSignal<MiniInfocardEstateSale> = input.required();
    imagePackages: InputSignal<ImagePackage[]> = input.required();
    showAttachedInfo: boolean = false;
    solutionPage = SolutionPage.EstateSaleMap;

    private readonly authService = inject(EvAuthorizationService);
    private readonly linkHelperService = inject(LinkHelperService);
    private readonly mapActionsService: MapActionsService = inject(MapActionsService);
    private readonly mapStateService: MapStateService = inject(MapStateService);
    private readonly marketSalesDataService = inject(MarketSalesDataService);
    readonly selectedFavoriteListStore = inject(SelectedFavoriteListsStore);
    readonly favoriteListDataService = inject(FavoriteListDataService);
    leftSidebarVisible = inject(LeftSidebarService).isOpen;
    hasFavoritelistRole = toSignal(this.authService.hasPermission(EVServiceType.EvWeb_FavoriteList));
    inCart = computed(() => this.selectedFavoriteListStore.units().includes(this.selectedSale().unitId));
    mapState = toSignal(this.mapStateService.mapState$)

    constructor(){
        effect(() => {
            if(this.selectedSale()) {
                this.leftSidebarVisible.set(true);
            }                
        })
    }

    protected menuItems = computed(() => {
        const keyInfo = this.selectedSale();

        if (!keyInfo) {
            return [];
        }

        const linkParams = {
            cadastre: keyInfo.cadastre,
            position: { lat: keyInfo.latitude, lng: keyInfo.longitude },
            organizationNumber: keyInfo.organizationNumber,
            unitId: keyInfo.unitId,
            estateType: keyInfo.estateType,
            estateId: keyInfo.estateId,
            ownershipType: keyInfo.ownershipId,
            shareNumber: keyInfo.shareNumber,
            unitLinkType: UnitLinkType.Residential,
        };
        let x = this.linkHelperService.getLinks(SolutionPage.EstateSaleMap, linkParams);
        return x;
    });

    centerMap(): void {
        this.mapActionsService.centerOn({ lat: this.selectedSale().latitude, lng: this.selectedSale().longitude });
    }

    showInMap(): void {
        this.mapActionsService.makeOutline({ lat: this.selectedSale().latitude, lng: this.selectedSale().longitude });
    }

    close() {
        this.marketSalesDataService.selectedSale.set(null);
        this.mapActionsService.removeEstatePolygon();
    }

    showCartIcon(keyInfo: MiniInfocardEstateSale): boolean {
        if (keyInfo.isAnyEstate) {
            return false;
        }

        if (keyInfo.ownershipId === EVOwnership.Obl_leilighet || keyInfo.ownershipId === EVOwnership.Aksjeleilighet || keyInfo.ownershipId === EVOwnership.Borettslag) {
            return false;
        }

        if (this.mapStateService.getCurrentMapMode() === MapMode.Market && (
            keyInfo.saleCategory === EVSaleCategoryType.OvertagelseAvBorettslagsandel
            || keyInfo.saleCategory === EVSaleCategoryType.TinglystOvertagelseAvBorettslagsandel
            || keyInfo.saleCategory === EVSaleCategoryType.Ukjent)) {
            return false;
        }

        return true;
    }
    
    modifyCart(e: MouseEvent) {
        e.stopPropagation();
        if (this.inCart()) {
            this.favoriteListDataService.removeUnit(this.selectedSale().unitId);
        } else {
            this.favoriteListDataService.addUnit(this.selectedSale().unitId, null);
        }
    }
}
